/**
 * Loads i18next translation resources. English is statically imported for
 * instant fallback. Other locales are loaded on demand via dynamic import()
 * so each locale becomes a separate bundle chunk, keeping the initial payload
 * small as we scale to 20+ languages.
 *
 * The import statements and the `localeLoaders` object between the
 * "BEGIN GENERATED" and "END GENERATED" markers are generated by
 * `bazel run i18n`. Do not edit them by hand.
 */

import type { Locale } from "./locales";
import { DEFAULT_LOCALE } from "./locales";
import { ALL_NAMESPACES, type I18nNamespaceType } from "./namespaces";

type TranslationRecord = Record<string, string>;
type NamespaceMap = Partial<Record<I18nNamespaceType, TranslationRecord>>;

// --- BEGIN GENERATED EN IMPORTS ---
import enCustomerPortalShared from "./en/customer-portal-shared.json";
import enCustomerPortal from "./en/customer-portal.json";
import enI18n from "./en/i18n.json";
import enMerchantApp from "./en/merchant-app.json";
import enMerchantFulfillmentApp from "./en/merchant-fulfillment-app.json";
import enModel from "./en/model.json";
import enReturnApp from "./en/return-app.json";
import enWeb from "./en/web.json";
// --- END GENERATED EN IMPORTS ---

// --- BEGIN GENERATED EN MODULES ---
const enModules: Record<I18nNamespaceType, TranslationRecord> = {
  "customer-portal-shared": enCustomerPortalShared,
  "customer-portal": enCustomerPortal,
  "i18n": enI18n,
  "merchant-app": enMerchantApp,
  "merchant-fulfillment-app": enMerchantFulfillmentApp,
  "model": enModel,
  "return-app": enReturnApp,
  "web": enWeb,
};
// --- END GENERATED EN MODULES ---

// --- BEGIN GENERATED LOCALE LOADERS ---
const localeLoaders: Record<string, () => Promise<NamespaceMap>> = {
  de: () => import("./de/_bundle").then((m) => m.default),
  es: () => import("./es/_bundle").then((m) => m.default),
  fr: () => import("./fr/_bundle").then((m) => m.default),
  it: () => import("./it/_bundle").then((m) => m.default),
  pl: () => import("./pl/_bundle").then((m) => m.default),
  tr: () => import("./tr/_bundle").then((m) => m.default),
};
// --- END GENERATED LOCALE LOADERS ---

function mergeNamespaces(namespaceMap: NamespaceMap): TranslationRecord {
  const merged: TranslationRecord = {};
  for (const ns of ALL_NAMESPACES) {
    const data = namespaceMap[ns];
    if (data) {
      Object.assign(merged, data);
    }
  }
  return merged;
}

/**
 * Get English resources synchronously. Always available — no network request.
 */
export function getEnglishResources(): TranslationRecord {
  return mergeNamespaces(enModules);
}

/**
 * Load resources for a locale. English returns immediately; other locales
 * trigger a dynamic import that the bundler serves as a separate chunk.
 */
export async function loadLocaleResources(
  locale: Locale,
): Promise<TranslationRecord> {
  if (locale === DEFAULT_LOCALE) {
    return getEnglishResources();
  }
  const loader = localeLoaders[locale];
  if (!loader) {
    return getEnglishResources();
  }
  const namespaceMap = await loader();
  return mergeNamespaces(namespaceMap);
}
